'use client';

import { graphql, useStaticQuery } from 'gatsby';
import React, { createContext, type FC, type ReactNode, useMemo, useState } from 'react';

import { int } from '@/utils/format';

type FormSettings = GraphqlSelect.FormSettings<'currency' | 'amountMin' | 'amountDefault' | 'amountMax' | 'amountStep'>;

interface CalculatorProps {
    children: ReactNode;
}

interface CalculatorContextFields {
    amount: number;
    changeAmount: (amount: number | string, step?: number) => number;
    formatCurrency: (amount: number) => string;
}

export const CalculatorContext = createContext({} as CalculatorContextFields & FormSettings);

export const Calculator: FC<CalculatorProps> = ({ children }) => {
    const { form } = useStaticQuery<{ form: FormSettings }>(graphql`
        query Calculator {
            form: formSettings {
                currency
                amountMin
                amountDefault
                amountMax
                amountStep
            }
        }
    `);

    const [amount, setAmount] = useState(form.amountDefault);

    const value = useMemo(() => {
        const changeAmount: CalculatorContextFields['changeAmount'] = (value, step = 0) => {
            const currentValue = Number.parseInt(value.toString(), 10) + step;
            const result =
                !Number.isSafeInteger(currentValue) || currentValue > form.amountMax
                    ? form.amountMax
                    : currentValue < form.amountMin
                      ? form.amountMin
                      : currentValue - (currentValue % form.amountStep);

            setAmount(result);

            return result;
        };

        const formatCurrency: CalculatorContextFields['formatCurrency'] = (value) => {
            return `${int.format(value)} ${form.currency}`;
        };

        return {
            ...form,
            amount,
            changeAmount,
            formatCurrency,
        };
    }, [form, amount]);

    return <CalculatorContext.Provider value={value}>{children}</CalculatorContext.Provider>;
};
